import React, { useState, useRef } from 'react';
import { 
  Box, 
  Card, 
  CardContent,
  Button,
  ButtonGroup,
} from '@mui/material';
import { ApiResponse } from '../../types/api';
import { FilterControls } from '../../components/FilterControls';
import { ExportService } from '../../services/exportService';
import { GenericTable } from '../../components/GenericTable';
import { GenericBarChart } from '../../components/GenericBarChart';
import { GenericCurveChart } from '../../components/GenericCurveChart';
import {
  transformProgressTableData,
  progressTableColumns,
  transformProgressChartData,
  transformProgressCurveData,
  getProgressRowBackgroundColor,
  getProgressRowTextColor,
  getProgressCellBackgroundColor,
  getProgressCellTextColor
} from '../../transforms/progressTransforms';
import { useSearchParams } from 'react-router-dom';

interface ProgressChartViewProps {
  data: ApiResponse;
}

const exportService = new ExportService();

export const ProgressChartView: React.FC<ProgressChartViewProps> = ({ data }) => {
  const [searchParams] = useSearchParams();
  const hideDownloadOptions = searchParams.get('hideDownloadOptions') || 'false';
  const [currentView, setCurrentView] = useState<'chart' | 'table' | 'curve'>('chart');
  const [viewLevel, setViewLevel] = useState<'all' | 'year' | 'week' | 'day'>('week');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });

  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const curveRef = useRef<HTMLDivElement>(null);

  const tableData = transformProgressTableData(data, viewLevel, dateRange);
  const chartData = transformProgressChartData(data, viewLevel, dateRange);
  const curveData = transformProgressCurveData(data, viewLevel, dateRange);

  const handleExportExcel = async () => {
    try {
      await exportService.exportToExcelV2(
        progressTableColumns.map(h => h.label),
        tableData.map((data) => 
          [data.Periodo, `${data.Programado}`, `${data.Ejecutado}`, `${data.Proyectado}`]
        ),
        `${data.Description}_progreso`
      )
    } catch (error) {
      alert('Error al exportar a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      let elementRef = chartRef;
      if (currentView === 'curve') elementRef = curveRef;
      if (!elementRef.current) {
        return
      }

      await exportService.exportToPDF({
        ...data,
        meteredPerPeriodLists: data.meteredPerPeriodLists
      }, elementRef.current);
    } catch (error) {
      alert('Error al exportar a PDF');
    }
  };

  return (
    <Box sx={{ width: '100%', margin: '0 auto' }}>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '1rem', pb: '1rem' }}>
            <strong>{`${data.Description} [${data.UnitMeasurement}]`}</strong>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '0.75rem' }}>
            <Box>
              <strong>Inicio:</strong> {data.strStart} | 
              <strong> Fin:</strong> {data.strEnd} | 
              <strong> Duración:</strong> {data.Duration}d
            </Box>
            <Box>
              <strong>Total:</strong> {data.Metered} | 
              <strong> Ejecutado:</strong> {data.ExecutedTotal} | 
              <strong> Por ejecutar:</strong> {data.RestTotal} | 
              <strong> Proyectado:</strong> {data.ProjectedTotal}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <FilterControls
        defaultViewLevel='week'
        minDate={data?.strStart}
        maxDate={data.strEnd}
        viewLevel={viewLevel}
        onViewLevelChange={setViewLevel}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        showAllOption={currentView === 'table'}
      />

      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => setCurrentView('chart')}
                variant={currentView === 'chart' ? 'contained' : 'outlined'}
              >
                Gráfico
              </Button>
              <Button 
                onClick={() => setCurrentView('curve')}
                variant={currentView === 'curve' ? 'contained' : 'outlined'}
              >
                Curva S
              </Button>
              <Button 
                onClick={() => setCurrentView('table')}
                variant={currentView === 'table' ? 'contained' : 'outlined'}
              >
                Tabla
              </Button>
            </ButtonGroup>

            {hideDownloadOptions === 'false' && <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              {currentView === 'table' && <Button size="small" variant="outlined" onClick={handleExportExcel}>
                Excel
              </Button>}
              {currentView !== 'table' && <Button size="small" variant="outlined" onClick={handleExportPDF}>
                PDF
              </Button>}
            </Box>}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          {currentView === 'chart' && (
            <div ref={chartRef}>
              <GenericBarChart
                labels={chartData.labels}
                datasets={chartData.datasets}
                title="Progreso por Período"
                yAxisTitle={data.UnitMeasurement}
                maxValue={chartData.maxValue}
                shouldUseHorizontalLabels={chartData.shouldUseHorizontalLabels}
                height={400}
              />
            </div>
          )}
          
          {currentView === 'table' && (
            <div ref={tableRef}>
              <GenericTable
                data={tableData}
                columns={progressTableColumns}
                showHierarchy={true}
                getRowBackgroundColor={getProgressRowBackgroundColor}
                getRowTextColor={getProgressRowTextColor}
                getCellBackgroundColor={getProgressCellBackgroundColor}
                getCellTextColor={getProgressCellTextColor}
              />
            </div>
          )}
          
          {currentView === 'curve' && (
            <div ref={curveRef}>
              <GenericCurveChart
                labels={curveData.labels}
                datasets={curveData.datasets}
                title="Curva S - Progreso Acumulado"
                yAxisTitle="Valor Acumulado"
                maxValue={curveData.maxValue}
                shouldUseHorizontalLabels={curveData.shouldUseHorizontalLabels}
                height={400}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};